"use client";

import { type CSSProperties, useEffect, useState } from "react";
import Link from "next/link";
import { TwinLiveMark } from "@product/components/app/twin/twin-live-mark";
import { BAR_FOCUS_CLS, NavDesktopMenu, type NavGroupId, NavMobileSheet } from "./nav-menu";
import {
  ACCENT_TEXT,
  DISPLAY,
  EDITORIAL_ACTION_CLS,
  EDITORIAL_CONTAINER_CLS,
  GROUND,
  INK,
  mix,
  SURFACE_DARK,
} from "./theme";
import { ThemeSwatches, useThemePickerHotkey } from "./theme-switcher";
import type { Cta } from "./types";

/**
 * Editorial top nav: the mark, four grouped menus (Product, Solutions,
 * Resources, Company) with a live feature card in each panel, and a single
 * CTA. From 768px the menus sit in the bar; below it a modal sheet holds the
 * same groups as accordions. Transparent with light text over a dark hero,
 * solidifying to a cream bar with ink text and a hairline once scrolled or
 * while a menu is open.
 */
export function EditorialNav({
  wordmark,
  cta,
  themePicker = false,
  solidFromStart = false,
  dark = false,
  placement = "fixed",
  review,
}: {
  wordmark: string;
  cta: Cta;
  /** Host the palette swatches in the nav's right cluster (top-right),
   *  replacing the floating ThemeSwitcher on pages that have a nav. */
  themePicker?: boolean;
  /** Force the solid cream bar from the top — for pages without a
   *  full-height dark hero behind the nav (e.g. about, blog, legal). */
  solidFromStart?: boolean;
  /** Keep the complete navigation chrome on the editorial dark surface. */
  dark?: boolean;
  /** `inline` draws the bar in flow, for the concept board. */
  placement?: "fixed" | "inline";
  /** Concept board only: always show the phone layout, with the sheet drawn
   *  inside `container` and optionally open on one group. */
  review?: { container: HTMLElement | null; sheetOpen: boolean; defaultGroup: NavGroupId };
}) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(review?.sheetOpen ?? false);
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const solid = !dark && (solidFromStart || scrolled || menuOpen || sheetOpen);
  const phone = review !== undefined;
  // The palette picker is hidden from end users until the "t" hotkey
  // reveals it; only listen on pages that actually host the picker.
  const showPicker = useThemePickerHotkey(themePicker);

  useEffect(() => {
    if (solidFromStart || dark) return;
    const onScroll = () => {
      // Flip to the solid bar as the full-height dark hero leaves view.
      setScrolled(window.scrollY > Math.max(80, window.innerHeight - 96));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dark, solidFromStart]);

  // The sheet is the phone layout; widening past it closes the sheet.
  useEffect(() => {
    if (phone) return;
    const closeAtDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setSheetOpen(false);
    };
    const media = window.matchMedia("(min-width: 768px)");
    media.addEventListener("change", closeAtDesktop);
    return () => media.removeEventListener("change", closeAtDesktop);
  }, [phone]);

  const fg = solid ? INK : GROUND;
  const navBackground = dark ? SURFACE_DARK : solid ? GROUND : "transparent";
  const navRule = dark
    ? mix("--theme-ground", 14)
    : solid
      ? mix("--theme-ink", 12)
      : "transparent";

  return (
    <header
      className={`${placement === "fixed" ? "fixed inset-x-0 top-0" : "relative w-full"} z-50 transition-colors duration-300`}
      style={
        {
          "--nav-fg": fg,
          backgroundColor: navBackground,
          borderBottom: `1px solid ${navRule}`,
          backdropFilter: solid || dark ? "blur(8px)" : "none",
        } as CSSProperties
      }
    >
      <div className={`flex min-h-17 items-center justify-between ${phone ? "px-5" : EDITORIAL_CONTAINER_CLS}`}>
        {/* The six-ring mark carries the brand; the wordmark string stays as
            the link's accessible name. Transparent ground works over both the
            dark hero and the solid cream bar. It works for ten seconds on
            arrival, then rests. */}
        <Link href="/" aria-label={wordmark} className={`flex items-center ${BAR_FOCUS_CLS}`} style={DISPLAY}>
          <TwinLiveMark className="size-7" durationSeconds={10} />
        </Link>

        <div ref={setAnchor} className="flex items-center gap-4 self-stretch lg:gap-6">
          {phone ? null : (
            <NavDesktopMenu
              anchor={anchor}
              color={fg}
              className="hidden md:flex"
              onOpenChange={setMenuOpen}
            />
          )}

          {showPicker && <ThemeSwatches size="sm" />}

          <a
            href={cta.href}
            className={`${EDITORIAL_ACTION_CLS} ${phone ? "hidden" : "max-sm:hidden"} ${BAR_FOCUS_CLS}`}
            style={{
              backgroundColor: solid ? SURFACE_DARK : GROUND,
              color: solid ? GROUND : ACCENT_TEXT,
            }}
          >
            {cta.label}
          </a>

          <NavMobileSheet
            cta={cta}
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            color={fg}
            triggerClassName={phone ? "-mr-2" : "-mr-2 md:hidden"}
            contained={phone}
            container={review?.container ?? null}
            defaultGroup={review?.defaultGroup ?? null}
          />
        </div>
      </div>
    </header>
  );
}
